import type {CreateIssueMutationMutation} from '@github-ui/server-persisted-queries/types/createIssueMutation'

import type {
  UseHandleTemplateChangeIssueFormFragment,
  UseHandleTemplateChangeIssueTemplateFragment,
} from '../use-handle-template-change'

type CreatedIssueResponse = NonNullable<NonNullable<CreateIssueMutationMutation['createIssue']>['issue']>
type CreatedIssueRepository = CreatedIssueResponse['repository']

export type CreatedIssue = Omit<CreatedIssueResponse, 'repository'> & {
  readonly repository: Omit<CreatedIssueRepository, 'owner'> & {
    readonly owner: Pick<CreatedIssueRepository['owner'], 'id' | 'login'>
  }
}

export type OnCreateProps = {
  issue: CreatedIssue
  createMore: boolean
}

/** Sub-issue create lifecycle; `tempId` correlates the phases so the temp node settles to the real one. */
export type SubIssueOptimisticCreateEvent =
  | {type: 'optimistic'; tempId: string; title: string}
  | {type: 'confirmed'; tempId: string; issue: CreatedIssue}
  | {type: 'failed'; tempId: string}

type IssueFormData = UseHandleTemplateChangeIssueFormFragment
type IssueTemplateData = UseHandleTemplateChangeIssueTemplateFragment

type BlankIssueData = {
  __typename: 'BlankIssue'
  __id: 'BLANK_ISSUE'
  title: ''
  body: ''
  assignees: {
    edges: []
  }
  labels: {
    edges: []
  }
}
export type IssueCreationData = BlankIssueData | IssueFormData | IssueTemplateData

export type IssueCreatePayload = {
  name: string
  fileName: string
  kind: IssueCreationKind
  data: IssueCreationData
}

export const BLANK_ISSUE = 'Blank issue'
export const BLANK_ISSUE_ID = 'BLANK_ISSUE'

export function instanceOfIssueFormData(data: IssueCreationData): data is IssueFormData {
  return data.__typename === 'IssueForm'
}

export function instanceOfIssueTemplateData(data: IssueCreationData): data is IssueTemplateData {
  return data.__typename === 'IssueTemplate'
}

export function instanceOfBlankIssueData(data: IssueCreationData): data is BlankIssueData {
  return data.__typename === 'BlankIssue'
}

export const IssueCreationKind = {
  IssueForm: 'IssueForm',
  IssueTemplate: 'IssueTemplate',
  ContactLink: 'ContactLink',
  BlankIssue: 'BlankIssue',
} as const

export type IssueCreationKind = (typeof IssueCreationKind)[keyof typeof IssueCreationKind]

const blankIssue: IssueCreatePayload = {
  name: BLANK_ISSUE,
  fileName: BLANK_ISSUE_ID,
  kind: IssueCreationKind.BlankIssue,
  data: {
    __typename: 'BlankIssue',
    __id: BLANK_ISSUE_ID,
    title: '',
    body: '',
    assignees: {
      edges: [],
    },
    labels: {
      edges: [],
    },
  },
}

export function getBlankIssue(): IssueCreatePayload {
  return blankIssue
}

export type SubIssueSummaryData = {
  total: number
  completed: number
  percentCompleted: number
}
